import { useSaveData } from '@entities/common/savedData/index.ts';
import { exerciseApi } from '@entities/exercise-video';
import { useTempExercises } from '@entities/exercise-video/model.ts';
import axios, { type AxiosProgressEvent } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { xhr } from '@shared/config';
import type { VideoPostRes } from '@shared/types/common';
import type { ExerciseVideo } from '@shared/types/exercises';
import { useCreateExerciseMutation } from '@src/api/api-hooks/exercises/useCreateExerciseMutation.ts';
import { useUpdateExerciseMutation } from '@src/api/api-hooks/exercises/useUpdateExerciseMutation.ts';
import { base64ToFile } from '@src/library/helpers/events/base64ToFile';
import type { ExerciseFormValues } from '../lib';

/**
 * Boomstream загружает фотку, из-за этого делаем задержку в 1.5 секунды
 */
export const useExerciseLoader = () => {
  const { data, doRemoveStorageData, doSetStorageData } = useSaveData<{ isLoading: boolean }>(
    'useExerciseLoader'
  );
  const fireLoader = useCallback(() => {
    doSetStorageData({ isLoading: true });
    setTimeout(doRemoveStorageData, 1500);
  }, [doSetStorageData, doRemoveStorageData]);
  return {
    fireLoader,
    isLoading: data?.isLoading,
  };
};

export const useExerciseUpload = (
  videoId: number,
  onCancel: () => void,
  onSuccess?: (id: number) => void
) => {
  const { fireLoader } = useExerciseLoader();
  const [progress, setProgress] = useState<AxiosProgressEvent>();
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'loading'>('idle');
  const isCanceled = useRef(false);
  const { addTempExercise } = useTempExercises();

  const { mutateAsync: createExerciseMutation } = useCreateExerciseMutation();
  const { mutateAsync: updateExerciseMutation } = useUpdateExerciseMutation();

  const instance = axios.create({
    withCredentials: false,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      setProgress(progressEvent);
    },
  });

  const create = async (data: ExerciseFormValues, isTempExercise?: boolean) => {
    setUploadStatus('loading');
    try {
      const exerciseData = new FormData();
      const exerciseConfig = { params: { module: data?.module } };

      if (data.screenshot_url instanceof File) {
        exerciseData.append('preview', data.screenshot_url);
      } else if (
        typeof data.screenshot_url === 'string' &&
        data.screenshot_url.includes('base64')
      ) {
        exerciseData.append('preview', base64ToFile(data.screenshot_url));
      }

      exerciseData.append('title', data.title);
      exerciseData.append('description', data.description);
      exerciseData.append('in_library', JSON.stringify(!isTempExercise));
      exerciseData.append('tags', JSON.stringify(data.tags));
      exerciseData.append('partitions', JSON.stringify(data.partitions));

      const res = await createExerciseMutation({
        exerciseData,
        params: exerciseConfig.params,
        isTempExercise,
      });
      if (!res.data) throw res;

      const formData = new FormData();
      formData.append('video_type', data.video_type);
      if (data.video_type === 'B') {
        formData.append('file', data.file);
        await instance.post(res.data.upload_url, formData);
        const checkRes = await xhr.get<ExerciseVideo>(`${exerciseApi}${res.data.id}/check/`);

        if (!checkRes.data) throw checkRes;
        !isCanceled.current && isTempExercise && addTempExercise(checkRes.data);
      } else {
        formData.append('embed_url', data.url);
        const exercisePatchRes = await updateExerciseMutation({
          id: String(res.data.id),
          exerciseData: formData,
          params: exerciseConfig.params,
        });
        if (!exercisePatchRes.data) throw exercisePatchRes;

        !isCanceled.current && isTempExercise && addTempExercise(exercisePatchRes.data);
      }

      setUploadStatus('idle');
      !isCanceled.current && onSuccess && onSuccess(res.data.id);
    } catch (error) {
      console.log(error);
      setUploadStatus('idle');
    }
  };

  const update = async (data: ExerciseFormValues, module?: any, isTempExercise?: boolean) => {
    setUploadStatus('loading');
    // console.log('data: ', data);

    try {
      const exerciseData = new FormData();
      const exerciseConfig = { params: { module: module } };

      data?.screenshot_url && exerciseData.append('preview', data?.screenshot_url);

      exerciseData.append('title', data.title);
      exerciseData.append('description', data.description);
      exerciseData.append('in_library', JSON.stringify(!isTempExercise));
      exerciseData.append('tags', JSON.stringify(data.tags));
      exerciseData.append('partitions', JSON.stringify(data.partitions));

      exerciseData.append('video_type', data.video_type);

      if (data.video_type === 'B') {
        if (data.file instanceof File) {
          const res = await xhr.get<VideoPostRes>(`${exerciseApi}${videoId}/new/`);
          if (!res.data) throw res;
          const formData = new FormData();
          formData.append('file', data.file);
          await instance.post(res.data.upload_url, formData);
          const checkRes = await xhr.get<ExerciseVideo>(`${exerciseApi}${videoId}/check/`);
          if (!checkRes.data) throw checkRes;
        }

        const patchRes: { data?: any } = isCanceled.current
          ? {}
          : await updateExerciseMutation({
              id: String(videoId),
              exerciseData: exerciseData,
              params: exerciseConfig.params,
            });

        if (!patchRes.data) throw patchRes;

        setUploadStatus('idle');
        fireLoader();
        !isCanceled.current && onSuccess && onSuccess(videoId);
      } else {
        exerciseData.append('embed_url', data.url);
        const response = await updateExerciseMutation({
          id: String(videoId),
          exerciseData: exerciseData,
          params: exerciseConfig.params,
        });
        if (!response.data) throw response;
        setUploadStatus('idle');
        !isCanceled.current && onSuccess && onSuccess(response.data.id);
      }
    } catch (error) {
      console.log(error);
      setUploadStatus('idle');
    }
  };

  const upload = (data: ExerciseFormValues, module?: any, isTempExercise?: boolean) => {
    isNaN(videoId) ? create(data, isTempExercise) : update(data, module, isTempExercise);
  };

  return {
    upload,
    progress,
    uploadStatus,
    doAbortLoading: () => {
      isCanceled.current = true;
      onCancel && onCancel();
    },
  };
};
